import React from "react";
import { SmlAsset, SmlJsonQueryFilter, SmlJsonQuery } from "@src/types/intelligence-search";
import { FormInstance } from "antd/lib/form";
import { Form, Input } from "antd";
import { IntelligenceFormFieldsEnum } from "@src/pages/intelligence-search/enum";
import { SearchTemplate, TSearchValue } from "@src/components/intelligence-search/search-template";
import { useWatchFormWithInit } from "@src/hooks/form/use-watch-form-with-init";
import { useManageSearchTemplate } from "@src/hooks/intelligence-search/use-manage-search-template";
import {
    convertQueryFilterToSearchValues,
    convertSearchValuesToQueryFilter,
} from "../../../../util/intelligence-search/transform";

export const SearchComponentSinger: React.FC<{
    enoughRequireField: boolean;
    handleGeneratePayload: ({ filters }: { filters?: SmlJsonQueryFilter[] }) => SmlJsonQuery;
    form: FormInstance;
    setIsUserChangeFirstTime: React.Dispatch<React.SetStateAction<boolean>>;
    productCode?: string;
    startDate?: string;
    endDate?: string;
}> = ({
    enoughRequireField,
    handleGeneratePayload,
    form,
    setIsUserChangeFirstTime,
    productCode,
    startDate,
    endDate,
}) => {
    const filtersWatch = useWatchFormWithInit(IntelligenceFormFieldsEnum.FILTERS_SELECTED, form);
    const filterValues = React.useMemo(() => {
        return filtersWatch || [];
    }, [filtersWatch]);

    React.useEffect(() => {
        setValues(convertQueryFilterToSearchValues(filterValues));
    }, [filterValues]);

    const { values, setValues, options, optionsLoading, addNewOptionProps } = useManageSearchTemplate({
        productCode: productCode,
        startDate,
        endDate,
        isPreventFetchApi: !enoughRequireField,
        labelProps: {
            transformValueToFilter: (values: TSearchValue[]) => {
                return convertSearchValuesToQueryFilter(values);
            },
            disabled(metaItem, currentFilterValues) {
                const isHasUnlikelyReasons = Boolean(
                    metaItem?.unlikely_reasons && metaItem?.unlikely_reasons?.length > 0,
                );
                const filterWithMeta = currentFilterValues.filter(
                    (f: SmlJsonQueryFilter) => f.meta?.name === metaItem.name,
                );
                const disabledLabelOnly =
                    isHasUnlikelyReasons || filterWithMeta.some((f: SmlJsonQueryFilter) => !f.operator);
                const disabledFilterOnly =
                    isHasUnlikelyReasons || filterWithMeta.some((f: SmlJsonQueryFilter) => !!f.operator);

                return {
                    isCustomMetric: filterWithMeta.some((f: SmlJsonQueryFilter) => f.isCustomMetric),
                    disabledFull: isHasUnlikelyReasons,
                    disabledLabelOnly,
                    disabledFilterOnly,
                    hasDisabled: isHasUnlikelyReasons || disabledLabelOnly || disabledFilterOnly,
                    unlikelyReasons: metaItem?.unlikely_reasons ?? [],
                };
            },
        },
        smtJsonQueryPayload: handleGeneratePayload({ filters: filterValues }),
        additionalOnChange: (values) => {
            form.setFieldValue(IntelligenceFormFieldsEnum.FILTERS_SELECTED, convertSearchValuesToQueryFilter(values));
        },
        addNewOptionProps: {
            handleFetchDryRun: (filters, dryRunCheck) => {
                return dryRunCheck(handleGeneratePayload({ filters }));
            },
            currentFilterValues: filterValues,
            onSuccess(newFilterSelected) {
                form.setFieldValue(IntelligenceFormFieldsEnum.FILTERS_SELECTED, newFilterSelected);
                setValues(convertQueryFilterToSearchValues(newFilterSelected));
            },
        },
    });

    const platformWatch = Form.useWatch(IntelligenceFormFieldsEnum.PLATFORM, form);
    const timeRangeWatch = Form.useWatch(IntelligenceFormFieldsEnum.TIME_RANGE, form);
    return (
        <div>
            <SearchTemplate
                formInstance={form}
                value={values}
                onChange={(values) => {
                    setValues(values);
                    form.setFieldValue(
                        IntelligenceFormFieldsEnum.FILTERS_SELECTED,
                        convertSearchValuesToQueryFilter(values),
                    );
                    setIsUserChangeFirstTime(true);
                }}
                loading={optionsLoading}
                options={options}
                addNewOptionProps={addNewOptionProps}
                searchModalFilterValueProps={{
                    productCode,
                    platform: platformWatch,
                    timeRange: timeRangeWatch,
                }}
            />
            <Form.Item name={IntelligenceFormFieldsEnum.FILTERS_SELECTED} hidden>
                <Input />
            </Form.Item>
        </div>
    );
};
